import React, { useCallback, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router";

import { AppBreadcrumbs } from "@shell/components/AppBreadcrumbs";
import { getContainerStyles } from "@shell/styles/themeUtils";
import CustomerIdentityManagementPage from "src/domains/platform-products/pages/CustomerIdentityManagementPage";
import {
  ORGANIZATIONS,
  PLATFORM_PRODUCTS,
} from "src/platform/constants/routes";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { useRegistryApi } from "src/platform/hooks/useRegistryApi";

// ── Platform environment ────────────────────────────────────────────────────
// Derive from platform env, never hardcode "dev". The binding's environment
// field is the source of truth once a binding exists.
const PLATFORM_ENV = import.meta.env.VITE_PLATFORM_ENVIRONMENT ?? "dev";

// ── Product configuration ───────────────────────────────────────────────────

interface ProductConfig {
  name: string;
  category: string;
  description: string;
  serviceAlias: string;
  audience: string;
  scopes: string[];
  capabilities: string[];
  features: string[];
  activateNoun: string;
  credentialHeader: string;
  credentialEnvPrefix: string;
  downloadFileName: string;
}

const PRODUCT_CONFIGS: Record<string, ProductConfig> = {
  "llm-gateway": {
    name: "LLM Gateway",
    category: "AI infrastructure",
    description:
      "A managed, organization-scoped gateway for routing application requests to approved language-model backends.",
    serviceAlias: "llm-gateway",
    audience: "llm-gateway",
    scopes: ["model:ollama:glm-5.2"],
    capabilities: ["OpenAI-compatible API", "Usage visibility"],
    features: [
      "OpenAI-compatible application access through a Rusha-managed gateway.",
      "Organization-scoped product configuration and usage visibility.",
      "Credentials provisioned through the platform — any application in your org can connect.",
    ],
    activateNoun: "LLM Gateway",
    credentialHeader: "LLM Gateway Connection Credentials",
    credentialEnvPrefix: "LLM_GATEWAY",
    downloadFileName: "llm-gateway-credentials",
  },
  "customer-identity": {
    name: "Customer Identity",
    category: "Application identity",
    description:
      "A managed identity platform for customer applications — register OIDC/OAuth clients, manage user directories, and federate from external identity providers.",
    serviceAlias: "customer-identity",
    audience: "customer-identity",
    scopes: ["openid", "profile", "email"],
    capabilities: ["User directories", "OIDC and OAuth clients", "Federation"],
    features: [
      "Register OIDC/OAuth clients for your applications with public, confidential, or machine-to-machine flows.",
      "Manage user directories scoped to your organization.",
      "Federate from external identity providers (Google, GitHub, corporate SSO).",
    ],
    activateNoun: "Customer Identity",
    credentialHeader: "Customer Identity Connection Credentials",
    credentialEnvPrefix: "CUSTOMER_IDENTITY",
    downloadFileName: "customer-identity-credentials",
  },
};

// ── Types ───────────────────────────────────────────────────────────────────

interface ServiceBinding {
  id: number;
  workloadAlias: string | null;
  serviceAlias: string;
  environment: string;
  audience: string;
  requestedScopes: string[];
  status: string;
  keycloakClientId: string;
  openBaoPath: string;
  failureCode: string | null;
  failureMessage: string | null;
  readyAt: string | null;
}

interface ServiceBindingCredentials {
  clientId: string;
  clientSecret: string;
  tokenEndpoint: string;
  audience: string;
  scopes: string[];
  gatewayUrl: string;
}

// ── Tab panel helper ────────────────────────────────────────────────────────

const TabPanel: React.FC<{
  children: React.ReactNode;
  value: number;
  index: number;
}> = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box
      role="tabpanel"
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {children}
    </Box>
  );
};

// ── Detail row helper ───────────────────────────────────────────────────────

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <Stack
    direction="row"
    spacing={2}
    justifyContent="space-between"
    alignItems="center"
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      component="div"
      variant="body2"
      fontWeight={600}
      textAlign="right"
    >
      {value}
    </Typography>
  </Stack>
);

// ── Step helper for activation/connect guides ───────────────────────────────

const NumberedStep: React.FC<{
  number: number;
  title: string;
  description: string;
}> = ({ number, title, description }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: "primary.main",
        color: "primary.contrastText",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.875rem",
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {number}
    </Box>
    <Stack spacing={0.5}>
      <Typography variant="body2" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  </Stack>
);

// ── Main component ──────────────────────────────────────────────────────────

const PlatformProductDetailsPage: React.FC = () => {
  const { productKey } = useParams<{
    orgAlias: string;
    productKey: string;
  }>();

  // Route to the Customer Identity management page for that product type.
  // Customer Identity has a dedicated multi-entity management surface
  // (clients, users, providers) — not the single-binding LLM Gateway page.
  if (productKey === "customer-identity") {
    return <CustomerIdentityManagementPage />;
  }

  return <LlmGatewayProductPage />;
};

const LlmGatewayProductPage: React.FC = () => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias, productKey } = useParams<{
    orgAlias: string;
    productKey: string;
  }>();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [binding, setBinding] = useState<ServiceBinding | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [pathCopied, setPathCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);

  const { call: listBindings } = useRegistryApi<ServiceBinding[]>(
    "application",
    "listOrgServiceBindings",
  );
  const { call: createBinding } = useRegistryApi<ServiceBinding>(
    "application",
    "createOrgServiceBinding",
  );
  const { call: getCredentials } = useRegistryApi<ServiceBindingCredentials>(
    "application",
    "getOrgServiceBindingCredentials",
  );
  const { call: rotateBinding } = useRegistryApi<ServiceBinding>(
    "application",
    "rotateOrgServiceBinding",
  );
  const { call: revokeBinding } = useRegistryApi<ServiceBinding>(
    "application",
    "revokeOrgServiceBinding",
  );

  const config = productKey ? PRODUCT_CONFIGS[productKey] : undefined;

  const loadBinding = useCallback(async () => {
    if (!orgAlias || !config) return;
    setLoading(true);
    setLoadError(false);
    setForbidden(false);
    try {
      const result = await listBindings({
        pathParams: { alias: orgAlias },
      });
      if (result && result.length > 0) {
        const match = result.find(
          (b) => b.serviceAlias === config.serviceAlias,
        );
        setBinding(match ?? null);
      } else {
        setBinding(null);
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 403) {
        setForbidden(true);
      } else {
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [orgAlias, config, listBindings]);

  useEffect(() => {
    void loadBinding();
  }, [loadBinding]);

  // Poll while provisioning or rotating
  useEffect(() => {
    if (!binding || !orgAlias) return;
    if (
      binding.status !== "pending" &&
      binding.status !== "provisioning" &&
      binding.status !== "rotating"
    )
      return;
    let cancelled = false;
    const timer = setInterval(() => {
      if (cancelled) return;
      void loadBinding();
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [binding?.status, orgAlias, loadBinding]);

  const openConfirm = () => {
    setConfirmOpen(true);
    setActivateError(null);
  };

  const closeConfirm = () => {
    if (!activating) {
      setConfirmOpen(false);
    }
  };

  const handleActivate = async () => {
    if (!orgAlias || !config) return;

    setActivateError(null);
    setActivating(true);
    try {
      const env = binding?.environment ?? PLATFORM_ENV;
      const idempotencyKey = `${orgAlias}-${config.serviceAlias}-${env}`;
      const result = await createBinding({
        pathParams: { alias: orgAlias },
        body: {
          serviceAlias: config.serviceAlias,
          environment: env,
          audience: config.audience,
          requestedScopes: config.scopes,
          idempotencyKey,
        },
      });
      setBinding(result);
      setConfirmOpen(false);
    } catch {
      setActivateError(
        `We could not activate ${config.activateNoun}. Check your organization permissions and try again.`,
      );
    } finally {
      setActivating(false);
    }
  };

  const handleRotate = async () => {
    if (!orgAlias || !binding) return;
    setRotating(true);
    try {
      const result = await rotateBinding({
        pathParams: { alias: orgAlias, bindingId: String(binding.id) },
      });
      setBinding(result);
    } catch {
      setActivateError(
        "Could not rotate credentials. Ensure you have admin permissions.",
      );
    } finally {
      setRotating(false);
    }
  };

  const handleDeactivate = async () => {
    if (!orgAlias || !binding) return;
    setRevoking(true);
    try {
      await revokeBinding({
        pathParams: { alias: orgAlias, bindingId: String(binding.id) },
      });
      setBinding(null);
    } catch {
      setActivateError(
        "Could not deactivate the product. Ensure you have admin permissions.",
      );
    } finally {
      setRevoking(false);
    }
  };

  const handleDownloadCredentials = async () => {
    if (!orgAlias || !binding || !config) return;
    setDownloading(true);
    try {
      const creds = await getCredentials({
        pathParams: { alias: orgAlias, bindingId: String(binding.id) },
      });
      if (!creds) return;
      const envContent = [
        `# ${config.credentialHeader}`,
        `# Organization: ${orgAlias}`,
        `# Generated: ${new Date().toISOString()}`,
        ``,
        `${config.credentialEnvPrefix}_CLIENT_ID=${creds.clientId}`,
        `${config.credentialEnvPrefix}_CLIENT_SECRET=${creds.clientSecret}`,
        `${config.credentialEnvPrefix}_TOKEN_ENDPOINT=${creds.tokenEndpoint}`,
        `${config.credentialEnvPrefix}_AUDIENCE=${creds.audience}`,
        `${config.credentialEnvPrefix}_SCOPES=${creds.scopes.join(" ")}`,
        `${config.credentialEnvPrefix}_URL=${creds.gatewayUrl}`,
        ``,
        `# Example token exchange:`,
        `# curl -X POST ${creds.tokenEndpoint} \\`,
        `#   -d "grant_type=client_credentials" \\`,
        `#   -d "client_id=$${config.credentialEnvPrefix}_CLIENT_ID" \\`,
        `#   -d "client_secret=********************************************" \\`,
        `#   -d "audience=$${config.credentialEnvPrefix}_AUDIENCE" \\`,
        `#   -d "scope=$${config.credentialEnvPrefix}_SCOPES"`,
      ].join("\n");
      const blob = new Blob([envContent], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${config.downloadFileName}-${orgAlias}.env`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setActivateError(
        "Could not retrieve credentials. Ensure you have admin permissions and the binding is ready.",
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyPath = async () => {
    if (!binding) return;
    try {
      await navigator.clipboard.writeText(binding.openBaoPath);
      setPathCopied(true);
      setTimeout(() => setPathCopied(false), 2000);
    } catch {
      setActivateError("Could not copy the path to clipboard.");
    }
  };

  if (!orgAlias || !config) {
    return null;
  }

  // ── Lifecycle state helpers ───────────────────────────────────────────────
  const isReady = binding?.status === "ready";
  const isFailed = binding?.status === "failed";
  const isPending =
    binding?.status === "pending" || binding?.status === "provisioning";
  const isRotating = binding?.status === "rotating";
  const isRevoked = binding?.status === "revoked";

  const statusLabel = isReady
    ? "Active"
    : isPending
      ? "Provisioning"
      : isRotating
        ? "Rotating"
        : isFailed
          ? "Failed"
          : isRevoked
            ? "Deactivated"
            : "Available";
  const statusColor: "success" | "info" | "error" | "warning" = isReady
    ? "success"
    : isPending || isRotating
      ? "info"
      : isFailed || isRevoked
        ? "error"
        : "info";

  // ── Error / forbidden / loading states ────────────────────────────────────
  if (forbidden) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <AppBreadcrumbs
          items={[
            { label: "Organizations", to: ORGANIZATIONS.LIST.path },
            {
              label: orgAlias,
              to: ORGANIZATIONS.DETAILS.getPath(orgAlias),
            },
            {
              label: PLATFORM_PRODUCTS.CATALOG.title,
              to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
            },
            { label: config.name },
          ]}
        />
        <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
          <CardContent>
            <Stack
              spacing={2}
              alignItems="center"
              sx={{ textAlign: "center", py: 4 }}
            >
              <Typography variant="h5" fontWeight={700}>
                Permission required
              </Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={480}>
                You can discover this product, but organization administrator
                permission is required to manage it.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => appNavigation.goToPlatformProducts(orgAlias)}
                sx={{ textTransform: "none" }}
              >
                Back to product catalog
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <AppBreadcrumbs
          items={[
            { label: "Organizations", to: ORGANIZATIONS.LIST.path },
            {
              label: orgAlias,
              to: ORGANIZATIONS.DETAILS.getPath(orgAlias),
            },
            {
              label: PLATFORM_PRODUCTS.CATALOG.title,
              to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
            },
            { label: config.name },
          ]}
        />
        <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
          <CardContent>
            <Stack
              spacing={2}
              alignItems="center"
              sx={{ textAlign: "center", py: 4 }}
            >
              <Typography variant="h5" fontWeight={700}>
                {config.name} workspace unavailable
              </Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={480}>
                The workspace could not be loaded. Try again after the service
                recovers.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => void loadBinding()}
                sx={{ textTransform: "none" }}
              >
                Try again
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <AppBreadcrumbs
          items={[
            { label: "Organizations", to: ORGANIZATIONS.LIST.path },
            {
              label: orgAlias,
              to: ORGANIZATIONS.DETAILS.getPath(orgAlias),
            },
            {
              label: PLATFORM_PRODUCTS.CATALOG.title,
              to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
            },
            { label: config.name },
          ]}
        />
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Typography variant="h4" fontWeight={700}>
            Loading {config.name}…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Product management data is being retrieved for {orgAlias}.
          </Typography>
        </Stack>
      </Container>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  const tabs = ["Overview", "Credentials", "Usage", "Settings", "Audit"];

  // Derive gateway URL from binding data (not hardcoded)
  const gatewayUrl = binding
    ? `https://${config.serviceAlias}.${binding.environment}.rusha.tierravivaai.net`
    : `https://${config.serviceAlias}.${PLATFORM_ENV}.rusha.tierravivaai.net`;

  return (
    <Container sx={getContainerStyles(theme)}>
      <AppBreadcrumbs
        items={[
          { label: "Organizations", to: ORGANIZATIONS.LIST.path },
          {
            label: orgAlias,
            to: ORGANIZATIONS.DETAILS.getPath(orgAlias),
          },
          {
            label: PLATFORM_PRODUCTS.CATALOG.title,
            to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
          },
          { label: config.name },
        ]}
      />

      <Stack spacing={3}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ sm: "flex-start" }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.25rem",
                  bgcolor: "rgba(37, 99, 235, 0.2)",
                  border: "1px solid rgba(96, 165, 250, 0.35)",
                  color: "#bfdbfe",
                  flexShrink: 0,
                }}
              >
                {"\u2726"}
              </Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                {config.name}
              </Typography>
              <Chip
                label={statusLabel}
                color={statusColor}
                variant="outlined"
                size="small"
                sx={{ color: `${statusColor}.dark` }}
              />
            </Stack>
            <Typography variant="body1" color="text.secondary" maxWidth={720}>
              {config.description}
            </Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
            <DetailRow label="Organization" value={orgAlias} />
            <DetailRow label="Product status" value={statusLabel} />
          </Stack>
        </Stack>

        {/* ── Activation state (not yet active) ──────────────────────────── */}
        {!isReady && !isRotating && (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {isPending
                      ? "Activation in progress"
                      : isFailed
                        ? "Activation failed"
                        : isRevoked
                          ? `${config.name} is deactivated`
                          : `Activate ${config.name}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isPending
                      ? "The identity provisioner is creating your Keycloak client and credentials."
                      : isFailed
                        ? "The last activation attempt failed. You can retry below."
                        : isRevoked
                          ? "The product was deactivated. You can re-activate it to provision new credentials."
                          : `Activate ${config.name} to provision your organization's credentials.`}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexShrink={0}>
                  <Button
                    variant="contained"
                    onClick={openConfirm}
                    sx={{ textTransform: "none" }}
                  >
                    {isFailed
                      ? "Retry activation"
                      : isRevoked
                        ? `Re-activate ${config.name}`
                        : `Activate ${config.name}`}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => appNavigation.goToPlatformProducts(orgAlias)}
                    sx={{ textTransform: "none" }}
                  >
                    Back to products
                  </Button>
                </Stack>
              </Stack>

              {isFailed && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {binding?.failureMessage ??
                    "The last activation attempt failed. Retry to provision credentials."}
                </Alert>
              )}

              {activateError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {activateError}
                </Alert>
              )}

              {/* Activation steps for not-yet-activated state */}
              {!isPending && !isFailed && !isRevoked && (
                <Stack spacing={2.5} sx={{ mt: 3 }}>
                  <Divider />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Activation steps
                  </Typography>
                  <NumberedStep
                    number={1}
                    title="Confirm organization scope"
                    description={`Credentials will be provisioned for ${orgAlias}. Any application in your organization can use them.`}
                  />
                  <NumberedStep
                    number={2}
                    title="Review access boundary"
                    description="The gateway validates tokens issued for your organization. It does not identify individual applications."
                  />
                  <NumberedStep
                    number={3}
                    title="Download credentials"
                    description="After activation, download the connection credentials or reference the OpenBao path via ExternalSecret."
                  />
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Activating {config.name} provisions an org-scoped Keycloak
                      client with client_credentials grant. OIDC credentials are
                      written to OpenBao and projected into the product
                      namespace.
                    </Typography>
                  </Alert>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Rotating state ─────────────────────────────────────────────── */}
        {isRotating && (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack
                spacing={1}
                alignItems="center"
                sx={{ textAlign: "center", py: 3 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Rotating credentials…
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The identity provisioner is regenerating the client secret.
                  Existing tokens remain valid until expiry.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* ── Active workspace with tabs ─────────────────────────────────── */}
        {isReady && binding && (
          <Stack spacing={2}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              aria-label={`${config.name} management tabs`}
            >
              {tabs.map((label) => (
                <Tab key={label} label={label} sx={{ textTransform: "none" }} />
              ))}
            </Tabs>

            {/* ── Overview tab ────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={0}>
              <Stack spacing={2}>
                {/* Metrics */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Requests today
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                          —
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Input tokens
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                          —
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Gateway health
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          color="success.main"
                        >
                          Healthy
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                          Gateway overview
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          The product is active. Use the credentials to call the
                          gateway from any application.
                        </Typography>
                        <Stack spacing={1}>
                          <DetailRow
                            label="Gateway URL"
                            value={
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                              >
                                {gatewayUrl}
                              </Typography>
                            }
                          />
                          <DetailRow
                            label="Endpoint"
                            value={
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                              >
                                /v1/chat/completions
                              </Typography>
                            }
                          />
                          <DetailRow
                            label="Authentication"
                            value="OIDC client_credentials"
                          />
                          <DetailRow
                            label="Audience"
                            value={
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                              >
                                {binding.audience}
                              </Typography>
                            }
                          />
                          <DetailRow
                            label="Scopes"
                            value={
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                              >
                                {binding.requestedScopes.join(" ")}
                              </Typography>
                            }
                          />
                        </Stack>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Any application in {orgAlias} can use the gateway with
                          the org-scoped credentials. No per-application
                          integration is required.
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                          Recent activity
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText
                              primary="Product activated"
                              secondary={binding.readyAt ?? "—"}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Stack>
            </TabPanel>

            {/* ── Credentials tab ─────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={1}>
              <Stack spacing={2}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      sx={{ mb: 2 }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="h6" fontWeight={700}>
                          Connection credentials
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Use these credentials to call {config.name} from any
                          application in your organization.
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => void handleRotate()}
                          disabled={rotating}
                          sx={{ textTransform: "none" }}
                        >
                          {rotating ? "Rotating…" : "Rotate credentials"}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => void handleDeactivate()}
                          disabled={revoking}
                          sx={{ textTransform: "none" }}
                        >
                          {revoking ? "Deactivating…" : "Deactivate"}
                        </Button>
                      </Stack>
                    </Stack>

                    <Stack spacing={1.5}>
                      <DetailRow
                        label="Client ID"
                        value={
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{ wordBreak: "break-all" }}
                          >
                            {binding.keycloakClientId}
                          </Typography>
                        }
                      />
                      <DetailRow
                        label="Token endpoint"
                        value={
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{ wordBreak: "break-all" }}
                          >
                            {gatewayUrl.replace(config.serviceAlias, "idp")}
                            /realms/rusha-{binding.environment}
                            /protocol/openid-connect/token
                          </Typography>
                        }
                      />
                      <DetailRow
                        label="Audience"
                        value={
                          <Typography variant="body2" fontFamily="monospace">
                            {binding.audience}
                          </Typography>
                        }
                      />
                      <DetailRow
                        label="Scopes"
                        value={
                          <Typography variant="body2" fontFamily="monospace">
                            {binding.requestedScopes.join(" ")}
                          </Typography>
                        }
                      />
                      <DetailRow
                        label="Gateway URL"
                        value={
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{ wordBreak: "break-all" }}
                          >
                            {gatewayUrl}
                          </Typography>
                        }
                      />
                    </Stack>

                    {/* Same cluster / External deployment */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Stack spacing={2}>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={700}
                            sx={{
                              letterSpacing: "0.08em",
                              display: "block",
                              mb: 1,
                            }}
                          >
                            SAME CLUSTER (EXTERNALSECRET)
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography
                              variant="body2"
                              fontFamily="monospace"
                              sx={{
                                wordBreak: "break-all",
                                fontSize: "0.75rem",
                              }}
                            >
                              {binding.openBaoPath}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => void handleCopyPath()}
                              sx={{ textTransform: "none", flexShrink: 0 }}
                            >
                              {pathCopied ? "Copied!" : "Copy path"}
                            </Button>
                          </Stack>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: "block" }}
                          >
                            If your application is deployed on the same cluster,
                            reference this OpenBao path in an ExternalSecret to
                            project the credentials into your namespace.
                          </Typography>
                        </Box>
                        <Divider />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={700}
                            sx={{
                              letterSpacing: "0.08em",
                              display: "block",
                              mb: 1,
                            }}
                          >
                            EXTERNAL DEPLOYMENT
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="body2" color="text.secondary">
                              Download the full credentials including the client
                              secret as a .env file.
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => void handleDownloadCredentials()}
                              disabled={downloading}
                              sx={{ textTransform: "none", flexShrink: 0 }}
                            >
                              {downloading
                                ? "Downloading…"
                                : "Download credentials"}
                            </Button>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Rotate credentials</strong> regenerates the
                        client secret while preserving the client ID. Both
                        OpenBao paths are updated. <strong>Deactivate</strong>{" "}
                        revokes the Keycloak client and deletes all stored
                        credentials.
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>

                {/* How to connect */}
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      How to connect
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Use the credentials above to call the gateway from any
                      application.
                    </Typography>
                    <Stack spacing={2.5}>
                      <NumberedStep
                        number={1}
                        title="Exchange credentials for a token"
                        description={`POST to the token endpoint with grant_type=client_credentials, client_id, client_secret, audience=${binding.audience}, scope=${binding.requestedScopes.join(" ")}`}
                      />
                      <NumberedStep
                        number={2}
                        title="Call the gateway"
                        description={`Send the access token as a Bearer header to ${gatewayUrl}/v1/chat/completions with the X-Backend header`}
                      />
                      <NumberedStep
                        number={3}
                        title="The gateway validates the token"
                        description={`It checks the issuer, audience (${binding.audience}), and scopes. It does not identify which application is calling.`}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </TabPanel>

            {/* ── Usage tab ───────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="h6" fontWeight={700}>
                          Usage
                        </Typography>
                        <Chip
                          label="Last 24 hours"
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">
                            Requests
                          </Typography>
                          <Typography variant="h5" fontWeight={700}>
                            —
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">
                            Input tokens
                          </Typography>
                          <Typography variant="h5" fontWeight={700}>
                            —
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">
                            Output tokens
                          </Typography>
                          <Typography variant="h5" fontWeight={700}>
                            —
                          </Typography>
                        </Grid>
                      </Grid>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        Usage data is organization-scoped and may be delayed
                        while the billing meter reconciles.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        Data status
                      </Typography>
                      <Stack spacing={1}>
                        <DetailRow label="Last received" value="—" />
                        <DetailRow
                          label="Meter status"
                          value={
                            <Chip
                              label="No data"
                              size="small"
                              variant="outlined"
                            />
                          }
                        />
                        <DetailRow label="Unavailable periods" value="—" />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* ── Settings tab ────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={3}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Gateway settings
                  </Typography>
                  <Stack spacing={1}>
                    <DetailRow
                      label="Allowed model providers"
                      value="Managed by Rusha Platform"
                    />
                    <DetailRow
                      label="Default request policy"
                      value="Organization standard"
                    />
                  </Stack>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Settings changes require organization administrator
                      permission. This panel does not persist changes in the
                      current release.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </TabPanel>

            {/* ── Audit tab ───────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={4}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      Audit activity
                    </Typography>
                    <Chip
                      label="Organization scope"
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  {binding.readyAt && (
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Product activated"
                          secondary={binding.readyAt}
                        />
                      </ListItem>
                    </List>
                  )}
                  {!binding.readyAt && (
                    <Typography variant="body2" color="text.secondary">
                      No audit events recorded yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </TabPanel>
          </Stack>
        )}

        {/* ── Failed state with retry ────────────────────────────────────── */}
        {isFailed && (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Alert severity="error">
                {binding?.failureMessage ??
                  "The last activation attempt failed. Retry to provision credentials."}
              </Alert>
              {activateError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {activateError}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* ── Activation Confirmation Dialog ──────────────────────────────── */}
      <Dialog
        open={confirmOpen}
        onClose={closeConfirm}
        fullWidth
        maxWidth="sm"
        aria-labelledby="activation-confirm-title"
      >
        <DialogTitle id="activation-confirm-title">
          {isFailed
            ? `Retry ${config.name} activation?`
            : isRevoked
              ? `Re-activate ${config.name}?`
              : `Activate ${config.name}?`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This provisions an organization-scoped Keycloak client with
              client_credentials grant, writes OIDC credentials to OpenBao, and
              projects them into the product namespace. Any application in{" "}
              <strong>{orgAlias}</strong> can then use these credentials.
            </Typography>

            <DetailRow label="Organization" value={orgAlias} />
            <DetailRow label="Product" value={config.name} />
            <DetailRow label="Scopes" value={config.scopes.join(", ")} />

            {activateError && <Alert severity="error">{activateError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeConfirm}
            disabled={activating}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleActivate()}
            disabled={activating}
            sx={{ textTransform: "none" }}
          >
            {activating ? "Activating…" : "Confirm activation"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlatformProductDetailsPage;
